import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Patent Data Validation",
  "US Patent Deadline Verification",
  "Patent Portfolio Data Review",
  "IP Database Validation",
  "Patent Document & Record Verification",
  "Ongoing IP Data Quality Support",
  "Other / Not Sure Yet",
];

const VOLUMES = ["Under 100", "100 – 1,000", "1,000 – 10,000", "10,000+", "Not Sure Yet"];

const initial = { name: "", company: "", email: "", phone: "", service: "", records: "", message: "" };

const fieldClass =
  "rounded-none border-slate-300 bg-white h-12 text-sm focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 focus-visible:border-accent";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Request submitted", {
        description: "Thank you — we will respond to your consultation request shortly.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Submission failed", {
        description: "Please try again, or email us directly at info@certifyipglobal.com.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-slate-50 py-24 lg:py-32 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel index="06">Contact</SectionLabel>
            <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-8">
              Request a Consultation
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-10">
              Tell us about your patent data validation or deadline verification needs. We
              will review your requirements and respond with a structured approach.
            </p>
            <div className="border border-slate-200 bg-white p-8">
              <p className="font-monodata text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-4">Direct Email</p>
              <a
                href="mailto:info@certifyipglobal.com"
                data-testid="contact-email-link"
                className="inline-flex items-center gap-3 text-navy font-semibold hover:text-accent transition-colors duration-300 group"
              >
                <Mail className="w-5 h-5 text-accent" />
                <span className="font-monodata text-sm border-b border-transparent group-hover:border-accent transition-colors duration-300">
                  info@certifyipglobal.com
                </span>
              </a>
              <p className="mt-6 text-sm text-slate-500 leading-relaxed">
                Specialized IP data validation support — designed for law firms and corporate
                IP teams.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-slate-200 p-8 lg:p-12">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="cf-name" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Name *</Label>
                  <Input id="cf-name" data-testid="contact-name-input" required maxLength={120} value={form.name} onChange={set("name")} className={fieldClass} placeholder="Jane Smith" />
                </div>
                <div>
                  <Label htmlFor="cf-company" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Company *</Label>
                  <Input id="cf-company" data-testid="contact-company-input" required maxLength={160} value={form.company} onChange={set("company")} className={fieldClass} placeholder="Law firm or company" />
                </div>
                <div>
                  <Label htmlFor="cf-email" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Work Email *</Label>
                  <Input id="cf-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={fieldClass} placeholder="you@firm.com" />
                </div>
                <div>
                  <Label htmlFor="cf-phone" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Phone</Label>
                  <Input id="cf-phone" type="tel" data-testid="contact-phone-input" maxLength={40} value={form.phone} onChange={set("phone")} className={fieldClass} placeholder="+1 (___) ___-____" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Service Required *</Label>
                  <Select required value={form.service} onValueChange={(v) => set("service")(v)}>
                    <SelectTrigger data-testid="contact-service-select" className={fieldClass}>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Approximate Number of Records</Label>
                  <Select value={form.records} onValueChange={(v) => set("records")(v)}>
                    <SelectTrigger data-testid="contact-records-select" className={fieldClass}>
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      {VOLUMES.map((v) => (
                        <SelectItem key={v} value={v} data-testid={`records-option-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="cf-message" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Message *</Label>
                  <Textarea
                    id="cf-message"
                    data-testid="contact-message-input"
                    required
                    maxLength={4000}
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className="rounded-none border-slate-300 bg-white text-sm focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 focus-visible:border-accent resize-none"
                    placeholder="Describe your portfolio, database, or deadline-checking requirements…"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-8 inline-flex items-center gap-2 bg-navy text-white font-semibold px-8 py-4 text-sm hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 group"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Submit Request
                {!sending && <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
              <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                Your information is treated confidentially and used only to respond to your
                request. Submitting this form does not create an attorney-client relationship.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
